/**
 * DatePicker avec locale française
 * Affiche les dates au format JJ/MM/AAAA
 * Utilise react-day-picker avec date-fns locale fr
 */

import * as React from "react";
import { format, parse, isValid } from "date-fns";
import { fr } from "date-fns/locale";
import { CalendarIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

/**
 * DatePickerFr - Date picker avec format français JJ/MM/AAAA
 * @param {string} value - Date au format ISO (YYYY-MM-DD) ou Date object
 * @param {function} onChange - Callback avec date ISO (YYYY-MM-DD)
 * @param {string} placeholder - Placeholder text
 * @param {boolean} disabled - Disable the picker
 */
export function DatePickerFr({ 
  value, 
  onChange, 
  placeholder = "Sélectionner une date",
  disabled = false,
  className
}) {
  const [open, setOpen] = React.useState(false);
  
  // Parse value to Date object
  const dateValue = React.useMemo(() => {
    if (!value) return undefined;
    if (value instanceof Date) return value;
    // Handle ISO format (YYYY-MM-DD)
    const parsed = parse(value, "yyyy-MM-dd", new Date());
    return isValid(parsed) ? parsed : undefined;
  }, [value]);
  
  // Format for display (French format)
  const displayValue = dateValue 
    ? format(dateValue, "dd/MM/yyyy", { locale: fr })
    : null;
  
  // Handle date selection
  const handleSelect = (date) => {
    if (date) {
      // Return ISO format for backend compatibility
      onChange(format(date, "yyyy-MM-dd"));
    } else {
      onChange("");
    }
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          disabled={disabled}
          className={cn(
            "w-full justify-start text-left font-normal h-9",
            !dateValue && "text-muted-foreground",
            className
          )}
          data-testid="date-picker-trigger"
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {displayValue || <span>{placeholder}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={dateValue}
          onSelect={handleSelect}
          locale={fr}
          initialFocus
          captionLayout="dropdown-buttons"
          fromYear={1940}
          toYear={2050}
        />
      </PopoverContent>
    </Popover>
  );
}

/**
 * Format a date string to French display format
 * @param {string} dateStr - Date in ISO format (YYYY-MM-DD)
 * @returns {string} - Date in French format (JJ/MM/AAAA) or "-"
 */
export function formatDateFr(dateStr) {
  if (!dateStr) return "-";
  try {
    const parsed = parse(dateStr, "yyyy-MM-dd", new Date());
    if (!isValid(parsed)) {
      // Try parsing as ISO datetime
      const isoDate = new Date(dateStr);
      if (isValid(isoDate)) {
        return format(isoDate, "dd/MM/yyyy", { locale: fr });
      }
      return dateStr;
    }
    return format(parsed, "dd/MM/yyyy", { locale: fr });
  } catch {
    return dateStr;
  }
}
